import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Accessibility, 
  X, 
  ZoomIn, 
  ZoomOut, 
  Contrast, 
  MousePointer2,
  Type,
  Eye,
  FileText,
  RotateCcw
} from 'lucide-react';
import { Button } from './ui/button';

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    largePointer: false,
    highlightLinks: false,
    readableFont: false,
  });

  // Apply accessibility settings
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Large pointer
    if (settings.largePointer) {
      root.classList.add('large-pointer');
    } else {
      root.classList.remove('large-pointer');
    }
    
    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    // Readable font
    if (settings.readableFont) {
      root.classList.add('readable-font');
    } else {
      root.classList.remove('readable-font');
    }
  }, [settings]);

  const increaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 10, 150)
    }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 10, 80)
    }));
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const resetAll = () => {
    setSettings({
      fontSize: 100,
      highContrast: false,
      largePointer: false,
      highlightLinks: false,
      readableFont: false,
    });
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#000080] hover:bg-[#1E3A8A] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#00A878]/50"
        aria-label="Open accessibility menu"
        data-testid="accessibility-widget-button"
      >
        <Accessibility className="w-7 h-7" />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel - positioned above the button */}
          <div 
            className="fixed bottom-24 left-6 z-50 bg-white rounded-xl shadow-2xl w-80 max-h-[70vh] overflow-y-auto border border-slate-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="accessibility-title"
            data-testid="accessibility-panel"
          >
            {/* Header */}
            <div className="bg-[#000080] text-white p-4 rounded-t-xl flex items-center justify-between sticky top-0">
              <div className="flex items-center gap-2">
                <Accessibility className="w-5 h-5" />
                <h2 id="accessibility-title" className="text-base font-bold">Accessibility</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close accessibility menu"
                data-testid="close-accessibility-panel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              {/* Font Size Controls */}
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Type className="w-4 h-4 text-[#000080]" />
                  <span className="font-medium text-sm text-[#0F172A]">Text Size</span>
                  <span className="ml-auto text-xs text-[#334155]">{settings.fontSize}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={decreaseFontSize}
                    disabled={settings.fontSize <= 80}
                    className="flex-1 border-[#000080] text-[#000080] h-8 text-xs"
                    data-testid="decrease-font"
                  >
                    <ZoomOut className="w-3 h-3 mr-1" />
                    Smaller
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={increaseFontSize}
                    disabled={settings.fontSize >= 150}
                    className="flex-1 border-[#000080] text-[#000080] h-8 text-xs"
                    data-testid="increase-font"
                  >
                    <ZoomIn className="w-3 h-3 mr-1" />
                    Larger
                  </Button>
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-2">
                <AccessibilityToggleSmall
                  icon={Contrast}
                  label="High Contrast"
                  enabled={settings.highContrast}
                  onToggle={() => toggleSetting('highContrast')}
                  testId="toggle-high-contrast"
                />
                
                <AccessibilityToggleSmall
                  icon={MousePointer2}
                  label="Large Cursor"
                  enabled={settings.largePointer}
                  onToggle={() => toggleSetting('largePointer')}
                  testId="toggle-large-pointer"
                />
                
                <AccessibilityToggleSmall
                  icon={Eye}
                  label="Highlight Links"
                  enabled={settings.highlightLinks}
                  onToggle={() => toggleSetting('highlightLinks')}
                  testId="toggle-highlight-links"
                />
                
                <AccessibilityToggleSmall
                  icon={Type}
                  label="Readable Font"
                  enabled={settings.readableFont}
                  onToggle={() => toggleSetting('readableFont')}
                  testId="toggle-readable-font"
                />
              </div>

              {/* Reset Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full border-slate-300 text-slate-600 h-8 text-xs"
                onClick={resetAll}
                data-testid="reset-accessibility"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset All
              </Button>

              {/* Accessibility Statement Link */}
              <Link
                to="/accessibility"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 text-[#000080] hover:text-[#00A878] text-sm font-medium py-1 transition-colors"
                data-testid="accessibility-statement-link"
              >
                <FileText className="w-4 h-4" />
                Accessibility Statement
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Compact Toggle Component
const AccessibilityToggleSmall = ({ icon: Icon, label, enabled, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
      enabled 
        ? 'border-[#00A878] bg-[#ECFDF5]' 
        : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
    data-testid={testId}
    aria-pressed={enabled}
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
      enabled ? 'bg-[#00A878] text-white' : 'bg-[#F8FAFC] text-[#000080]'
    }`}>
      <Icon className="w-4 h-4" />
    </div>
    <span className="flex-1 font-medium text-sm text-[#0F172A]">{label}</span>
    <div className={`w-10 h-5 rounded-full flex items-center transition-colors flex-shrink-0 ${
      enabled ? 'bg-[#00A878]' : 'bg-slate-300'
    }`}>
      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
        enabled ? 'translate-x-5' : 'translate-x-0.5'
      }`} />
    </div>
  </button>
);

export default AccessibilityWidget;
